import React, {
  useState, useEffect, Fragment,
} from 'react';
import PropTypes from 'prop-types';
import { Button, Alert } from 'react-bootstrap';
import {
  getRequest, postRequest, deleteRequest,
} from '../../requestUtils';
import Loading from '../common/Loading';
import checkmarkImg from '../../../assets/images/check-mark.svg';
import infoIcon from '../../../assets/images/icons/blue-info-icon.svg';
import './InterviewQuestions.scss';

function InterviewQuestions({
  consultantName,
  proposalResourceId,
  interviewQuestionsUrl,
  isConsultant,
  csrfToken,
}) {
  const [interviewQuestions, setInterviewQuestions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchQuestions = () => {
    setLoading(true);
    setError('');
    getRequest(interviewQuestionsUrl, { resource_id: proposalResourceId })
      .then(({ data }) => {
        setInterviewQuestions(data.interview_question);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load questions.');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const generateInterviewQuestions = () => {
    setLoading(true);
    setError('');
    postRequest(
      interviewQuestionsUrl,
      { resource_id: proposalResourceId },
      csrfToken,
    ).then(({ data }) => {
      setInterviewQuestions(data.interview_question);
      setLoading(false);
    })
      .catch((e) => {
        setError('Failed to load questions.', e);
        setLoading(false);
      });
  };

  const handleFeedbackClick = (index, vote) => {
    const currentVote = interviewQuestions.feedback?.[index]?.[0];
    const InterviewQuestionFeedbackId = interviewQuestions.feedback?.[index]?.[1];
    if (currentVote === vote) {
      deleteRequest(
        `/interview_question_feedbacks/${InterviewQuestionFeedbackId}`,
        csrfToken,
      ).then(() => {
        setInterviewQuestions((prev) => ({
          ...prev,
          feedback: {
            ...prev.feedback,
            [index]: [null, null],
          },
        }));
        setLoading(false);
      }).catch((e) => {
        setError('Failed to record feedback', e);
        setLoading(false);
      });
    } else {
      postRequest(
        '/interview_question_feedbacks',
        {
          interview_question_id: interviewQuestions.id,
          question_index: index,
          feedback: vote,
        },
        csrfToken,
      ).then(({ data }) => {
        setInterviewQuestions((prev) => ({
          ...prev,
          feedback: data.feedback,
        }));
        setLoading(false);
      }).catch((e) => {
        setError('Failed to record feedback', e);
        setLoading(false);
      });
    }
  };

  const renderFeedback = (value, onClick) => (
    <div className="d-flex align-items-center">
      <span
        role="button"
        tabIndex={0}
        className="bg-body-tertiary py-1 px-3 rounded-start-pill"
        onClick={() => onClick('up')}
        style={{ cursor: 'pointer' }}
      >
        <i className={`fa${value === 'up' ? 's' : 'r'} fa-thumbs-up`} />
      </span>
      <div className="vr" />
      <span
        role="button"
        tabIndex={0}
        className="bg-body-tertiary py-1 px-3 rounded-end-circle"
        onClick={() => onClick('down')}
        style={{ cursor: 'pointer' }}
      >
        <i className={`fa${value === 'down' ? 's' : 'r'} fa-thumbs-down`} />
      </span>
    </div>
  );

  return (
    <div className="body-section interview-questions__container">
      <div className="interview-header fw-bold text-center mb-3">
        {isConsultant ? 'Get Ready for Your Interview' : `Interview Prep for ${consultantName}`}
      </div>

      <div className="info-blurb mb-4">
        <img src={infoIcon} alt="info-icon" />
        <div className="message">
          {isConsultant ? (
            <Fragment>
              These interview questions are generated using your profile information and the job you are applying for.
              They’re designed to help you reflect on your fit, highlight your strengths, and prepare for potential interview topics.
            </Fragment>
          ) : (
            <Fragment>
              These interview questions are generated using the consultant’s profile and the project details.
              They’re designed to help you quickly identify alignment, uncover key discussion points, and streamline your evaluation process.
            </Fragment>
          )}
        </div>
      </div>
      {loading && <Loading />}
      <div className="text-center">
        {!loading && Object.keys(interviewQuestions).length === 0 && (
          <Button onClick={generateInterviewQuestions} variant="primary" className="mt-3">
            {loading ? 'Generating...' : 'Generate Interview Questions'}
          </Button>
        )}
      </div>
      {!loading && Object.keys(interviewQuestions).length > 0 && (
        <Fragment>
          <div className="d-flex flex-column gap-4 mt-3">
            {interviewQuestions.questions.map((q, i) => {
              const [questionText, category] = q.split('**');
              return (
                <div className="d-flex gap-4 border-top pt-3" key={i}>
                  <div className="d-none d-md-flex fw-bolder text-center question-number">{`Question ${i + 1}`}</div>
                  <div className="question-text d-flex flex-column gap-2">
                    <div>{questionText}</div>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-bolder border border-1 rounded-pill p-2 question-category bg-success-subtle">
                        {category}
                      </div>
                      {renderFeedback(interviewQuestions?.feedback?.[i + 1]?.[0], (vote) => handleFeedbackClick(i + 1, vote))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {error && <Alert variant="danger" className="mt-3 text-center">{error}</Alert>}
        </Fragment>
      )}
    </div>
  );
}

InterviewQuestions.propTypes = {
  interviewQuestionsUrl: PropTypes.string.isRequired,
  proposalResourceId: PropTypes.string.isRequired,
  isConsultant: PropTypes.bool.isRequired,
  csrfToken: PropTypes.string.isRequired,
};

export default InterviewQuestions;
